import { decodeFunctionData, erc20Abi, type Abi, type Hex } from 'viem'
import { parseSiweMessage, validateSiweMessage } from 'viem/siwe'
import { config } from '../appConfig'
import { EVMChain } from '../interfaces/common'
import { EthTransaction, EthTransactionType, type ContractInfo } from '../interfaces/eth'
import { logger } from '../logger'
import { getEthProvider } from '../utils/getEthProvider'
import { GasCalculator } from './GasCalculator'

const TEST_ETH_ADDRESS = '0x343f1c4C2ac5750C9F9c22B2aC1aE93F4B9a6def'
export const ethAddressRegex = /^0x[0-9a-fA-F]{40}$/
const MAX_UINT256 = 0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffn

// keccak256("eip1967.proxy.implementation") - 1
const EIP1967_IMPLEMENTATION_SLOT =
  '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc'

// keccak256("org.zeppelinos.proxy.implementation")
const OZ_LEGACY_IMPLEMENTATION_SLOT =
  '0x7050c9e0f4ca769c69bd3a8ef740bc37934f8e2c036e5a723fd8ee048ed3f8c3'

const A_LOT_OF_ETH = 10n ** 25n
const DIAMOND_LOUPE_ABI = [
  {
    name: 'facetAddress',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: '_functionSelector', type: 'bytes4' }],
    outputs: [{ name: 'facetAddress_', type: 'address' }],
  },
] as const

// Gnosis Safe / SafeProxy stores its singleton (masterCopy) at storage slot 0
// and exposes it via masterCopy(). It uses neither EIP-1967 nor OZ slots.
const SAFE_MASTER_COPY_ABI = [
  {
    name: 'masterCopy',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
] as const

const extractAddress = (value?: `0x${string}`) => {
  if (!value || value === '0x') return null
  const addr = `0x${value.slice(-40)}`
  return addr === '0x0000000000000000000000000000000000000000' ? null : (addr as `0x${string}`)
}

/*
  Fetches contract implementation address
  if provided address is a proxy
 */
async function resolveImplementationAddress(
  rpcEndpoint: string,
  proxyAddress: `0x${string}`,
  selector?: `0x${string}`
): Promise<`0x${string}` | null> {
  const client = getEthProvider(rpcEndpoint)

  // EIP-1967
  const eip1967 = extractAddress(
    await client.getStorageAt({
      address: proxyAddress,
      slot: EIP1967_IMPLEMENTATION_SLOT,
    })
  )
  if (eip1967) return eip1967

  // Legacy OpenZeppelin / USDC
  const ozLegacy = extractAddress(
    await client.getStorageAt({
      address: proxyAddress,
      slot: OZ_LEGACY_IMPLEMENTATION_SLOT,
    })
  )
  if (ozLegacy) return ozLegacy

  // Fallback: implementation() function
  try {
    return await client.readContract({
      address: proxyAddress,
      abi: [
        {
          name: 'implementation',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ type: 'address' }],
        },
      ],
      functionName: 'implementation',
    })
  } catch {}

  // Diamond proxy fallback - Jumper.xyz uses diamond proxy
  if (selector) {
    try {
      const facetAddress = await client.readContract({
        address: proxyAddress,
        abi: DIAMOND_LOUPE_ABI,
        functionName: 'facetAddress',
        args: [selector],
      })

      if (facetAddress !== '0x0000000000000000000000000000000000000000') {
        return facetAddress
      }
    } catch {}
  }

  // Gnosis Safe proxy - resolve the singleton via masterCopy()
  try {
    const masterCopy = extractAddress(
      await client.readContract({
        address: proxyAddress,
        abi: SAFE_MASTER_COPY_ABI,
        functionName: 'masterCopy',
      })
    )
    if (masterCopy) return masterCopy
  } catch {}

  return null
}

/*
  Fetches contract information from Etherscan API
  to simulate the transaction and get the gas limit
  and functions parameters
*/
export const callContractFunction = async (
  address: string,
  data: string | undefined,
  value: string | undefined,
  from: string | undefined,
  gasLimit: string | undefined,
  chain: EVMChain
): Promise<ContractInfo | null> => {
  try {
    if (!data) return null

    const provider = getEthProvider(chain.rpcEndpoint)

    // Gas estimation only needs to/data/value, so do it independently of the
    // contract ABI. Many chains (e.g. HyperEVM) commonly expose unverified
    // contracts whose ABI can't be fetched from Etherscan — those calls must
    // still go through, they just lose the decoded params in the approval UI.
    let estimatedGas = BigInt(0)

    try {
      const estimationAccount = (from || TEST_ETH_ADDRESS) as `0x${string}`

      estimatedGas = await provider.estimateGas({
        to: address as `0x${string}`,
        data: data as Hex,
        account: estimationAccount,
        value: value ? BigInt(value) : 0n,
        // Override the sender balance so payable calls can still be simulated even if
        // the wallet does not currently have enough native token for value + gas.
        stateOverride: [
          {
            address: estimationAccount,
            balance: A_LOT_OF_ETH,
          },
        ],
      })
    } catch (error) {
      logger.debug('Error estimating gas for contract method', { originalError: error })
      // if gas estimation failed - use gas limit provided by dApp
      if (gasLimit) {
        try {
          estimatedGas = BigInt(gasLimit)
        } catch {
          throw Error('Invalid provided gas limit')
        }
      } else {
        throw Error('Failed to estimate gas and no gas limit was provided')
      }
    }

    const contractInfo: ContractInfo = {
      gasLimit: estimatedGas,
      value,
      contractAddress: address,
    }

    // Best-effort: decode the call via the verified ABI for a nicer approval UI.
    // Any failure here (unverified contract, undecodable data) must not block
    // the transaction — gas has already been estimated above.
    try {
      const implementation = await resolveImplementationAddress(
        chain.rpcEndpoint,
        address as `0x${string}`,
        data.slice(0, 10) as `0x${string}`
      )
      const abiAddress = implementation ?? address

      const response = await fetch(
        `https://api.etherscan.io/v2/api?chainid=${chain.chainId}&module=contract&action=getabi&address=${abiAddress}&apikey=${config.fetchEnv(
          'ETHERSCAN_SECRET'
        )}`
      )
      const abiResponse = await response.json()
      const parsedAbi = JSON.parse(abiResponse.result) as Abi

      const decoded = decodeFunctionData({
        abi: parsedAbi,
        data: data as Hex,
      })

      const abiItem = parsedAbi.find(
        (item: any) => item.type === 'function' && item.name === decoded.functionName
      ) as any

      if (abiItem) {
        contractInfo.name = abiItem.name
        contractInfo.functionType = abiItem.stateMutability
        contractInfo.inputs = abiItem.inputs
        contractInfo.params = decoded.args as any
      }
    } catch (error) {
      logger.debug('Could not decode contract call via ABI', { originalError: error })
    }

    return contractInfo
  } catch (error) {
    logger.debug('Error fetching contract info', { originalError: error })
  }

  return null
}

export const getGas = async (
  type: EthTransactionType,
  evmChain: EVMChain,
  ethAddress: string,
  transaction: EthTransaction
) => {
  const gasCalculator = new GasCalculator(evmChain)
  const gas = await gasCalculator.getEthGas(ethAddress, 1n)
  if (!gas || gas.maxFeePerGas == 0n) throw Error('Could not calculate network fee')

  switch (type) {
    case EthTransactionType.SEND_ETH:
      if (!gas.gasLimit) throw Error('Could not calculate network fee')
      return {
        gasLimit: gas.gasLimit,
        maxFeePerGas: gas.maxFeePerGas,
        maxPriorityFeePerGas: gas.maxPriorityFeePerGas,
      }
    default: // for contract interactions gas limit is calculated by simulating call
      const contractInfo = await callContractFunction(
        transaction.to,
        transaction.data,
        transaction.value,
        transaction.from,
        transaction.gasLimit || transaction.gas,
        evmChain
      )
      if (!contractInfo?.gasLimit) throw Error('Contract is not accessible')
      return {
        gasLimit: contractInfo.gasLimit,
        maxFeePerGas: gas.maxFeePerGas,
        maxPriorityFeePerGas: gas.maxPriorityFeePerGas,
        contractInfo,
      }
  }
}

export interface DecodedTxInfo {
  type: EthTransactionType
  receiver?: `0x${string}`
  sender?: `0x${string}`
  amount?: bigint
  spender?: `0x${string}`
  isUnlimited?: boolean
}

export const getEthTransactionInfo = (data?: string): DecodedTxInfo => {
  if (!data || data === '0x') {
    return {
      type: EthTransactionType.SEND_ETH,
    }
  }

  const selector = data.slice(0, 10).toLowerCase()

  try {
    switch (selector) {
      // transfer(address to, uint256 amount)
      case '0xa9059cbb': {
        const { args } = decodeFunctionData({
          abi: erc20Abi,
          data: data as `0x${string}`,
        })

        const [to, amount] = args as [`0x${string}`, bigint]

        return {
          type: EthTransactionType.TRANSFER,
          receiver: to,
          amount,
        }
      }

      // approve(address spender, uint256 amount)
      case '0x095ea7b3': {
        const { args } = decodeFunctionData({
          abi: erc20Abi,
          data: data as `0x${string}`,
        })

        const [spender, amount] = args as [`0x${string}`, bigint]

        return {
          type: EthTransactionType.APPROVE,
          spender,
          amount,
          isUnlimited: amount === MAX_UINT256,
        }
      }

      // transferFrom(address from, address to, uint256 amount)
      case '0x23b872dd': {
        const { args } = decodeFunctionData({
          abi: erc20Abi,
          data: data as `0x${string}`,
        })

        const [from, to, amount] = args as [`0x${string}`, `0x${string}`, bigint]

        return {
          type: EthTransactionType.TRANSFER_FROM,
          sender: from,
          receiver: to,
          amount,
        }
      }

      default:
        return {
          type: EthTransactionType.CONTRACT_CALL,
        }
    }
  } catch {
    return {
      type: EthTransactionType.CONTRACT_CALL,
    }
  }
}

export const hexToText = (hexString: string): string => {
  hexString = hexString.startsWith('0x') ? hexString.slice(2) : hexString

  return decodeURIComponent(hexString.replace(/\s+/g, '').replace(/[0-9a-f]{2}/g, '%$&'))
}

export const checkEthTx = (params: any, ethAccount: string) => {
  if (!params) throw Error('No params provided')
  if (!params.to) throw Error('No destination address')
  if (!params.from) throw Error('No sender address')
  if (!ethAddressRegex.test(params.to)) throw Error('Invalid address')
  if (!ethAddressRegex.test(params.from)) throw Error('Invalid address')
  if (params.from.toLowerCase() != ethAccount.toLowerCase()) throw Error('Invalid eth account')
  if (params.data && !params.data.startsWith('0x')) throw Error('Invalid data')
  if (params.type && params.type != '0x2' && params.type != '0x0')
    throw Error('Unsupported tx type')
  if (params.gasLimit) BigInt(params.gasLimit)
  if (params.maxFeePerGas) BigInt(params.maxFeePerGas)
  if (params.maxPriorityFeePerGas) BigInt(params.maxPriorityFeePerGas)
  if (params.nonce) BigInt(params.nonce)
  if (params.value) BigInt(params.value)
}

export const getERC20TokenMetadata = async (chain: EVMChain, address: `0x${string}`) => {
  const provider = getEthProvider(chain.rpcEndpoint)

  const [name, symbol, decimals] = await Promise.all([
    provider.readContract({
      address,
      abi: [
        {
          name: 'name',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ type: 'string' }],
        },
      ],
      functionName: 'name',
    }),
    provider.readContract({
      address,
      abi: [
        {
          name: 'symbol',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ type: 'string' }],
        },
      ],
      functionName: 'symbol',
    }),
    provider.readContract({
      address,
      abi: [
        {
          name: 'decimals',
          type: 'function',
          stateMutability: 'view',
          inputs: [],
          outputs: [{ type: 'uint8' }],
        },
      ],
      functionName: 'decimals',
    }),
  ])

  return { name, symbol, decimals }
}

export const checkSiweMessage = (message: string, ethAddress: string, dappHost: string) => {
  let siweMessage

  try {
    siweMessage = parseSiweMessage(hexToText(message))
    if (
      !validateSiweMessage({
        domain: dappHost,
        address: ethAddress as `0x${string}`,
        message: siweMessage,
      })
    )
      return undefined
  } catch (error) {
    return undefined // malformed or not siwe message
  }

  return siweMessage
}
